/**
 * Video file-type detection for the CLI.
 *
 * Maps a file extension to a video MIME type for the presigned-PUT Content-Type.
 * Videos are stored raw — never re-encoded — so the extension is authoritative
 * (mirrors the web upload, which trusts the browser's file type). Returns null
 * for anything that isn't a recognized video container, so the command can
 * reject non-video files with a clear message.
 *
 * @module
 */

/** Recognized video extensions → MIME type. */
const VIDEO_MIME: Record<string, string> = {
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  ogv: "video/ogg",
  ogg: "video/ogg",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
  "3gp": "video/3gpp",
  mpeg: "video/mpeg",
  mpg: "video/mpeg",
}

/** Recognized video extensions, single-sourced from the MIME map. */
const VIDEO_EXTENSIONS: ReadonlyArray<string> = Object.keys(VIDEO_MIME)

/** Comma-joined recognized extensions for help text and error messages. */
export const VIDEO_EXTENSIONS_LABEL: string = VIDEO_EXTENSIONS.join(", ")

/**
 * Map a file path's extension to a video MIME type, or null if the extension is
 * not a recognized video container.
 */
export const mimeFromVideoPath = (filePath: string): string | null => {
  const dot = filePath.lastIndexOf(".")
  if (dot < 0 || dot === filePath.length - 1) return null
  const ext = filePath.slice(dot + 1).toLowerCase()
  return VIDEO_MIME[ext] ?? null
}
